import { useState, useEffect } from 'react';

interface Category {
  id: number;
  name: string;
  sort: number;
  isEnabled: boolean;
  parentId: number | null;
  productCount: number;
}

interface CategoryFormModalProps {
  isOpen: boolean;
  category: Category | null;
  parentCategory: Category | null;
  onClose: () => void;
  onSave: (data: Partial<Category>) => void;
}

export default function CategoryFormModal({
  isOpen,
  category,
  parentCategory,
  onClose,
  onSave,
}: CategoryFormModalProps) {
  const [formData, setFormData] = useState<Partial<Category>>({
    name: '',
    sort: 999,
    isEnabled: true,
  });

  const [error, setError] = useState('');

  useEffect(() => {
    if (category) {
      setFormData(category);
    } else {
      setFormData({
        name: '',
        sort: 999,
        isEnabled: true,
      });
    }
    setError('');
  }, [category, isOpen]);

  const handleSave = () => {
    if (!formData.name) {
      setError('请输入分类名称');
      return;
    }

    if (formData.sort === undefined || formData.sort < 0) {
      setError('请输入有效的排序值');
      return;
    }

    onSave(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto">
      <div className="absolute inset-0 bg-black/50" onClick={onClose}></div>
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-xl font-semibold text-gray-900">
            {category ? '编辑分类' : parentCategory ? '添加下级分类' : '新建分类'}
          </h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {parentCategory && (
            <div className="bg-teal-50 border border-teal-200 text-teal-700 px-4 py-3 rounded-lg text-sm">
              上级分类：{parentCategory.name}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              分类名称 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="请输入分类名称"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              排序 <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={formData.sort}
              onChange={(e) => setFormData({ ...formData, sort: Number(e.target.value) })}
              placeholder="数字越小越靠前"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              分类状态 <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-4">
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  checked={formData.isEnabled === true}
                  onChange={() => setFormData({ ...formData, isEnabled: true })}
                  className="w-4 h-4 text-teal-500 border-gray-300 focus:ring-teal-500 cursor-pointer"
                />
                <span className="ml-2 text-sm text-gray-700">正常</span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  checked={formData.isEnabled === false}
                  onChange={() => setFormData({ ...formData, isEnabled: false })}
                  className="w-4 h-4 text-teal-500 border-gray-300 focus:ring-teal-500 cursor-pointer"
                />
                <span className="ml-2 text-sm text-gray-700">禁用</span>
              </label>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 pb-6 border-t border-gray-200 pt-4">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
          >
            取消
          </button>
          <button
            onClick={handleSave}
            className="px-6 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap"
          >
            保存
          </button>
        </div>
      </div>
    </div>
  );
}
